import { Stack, Tooltip, UnstyledButton } from "@mantine/core";
import { forwardRef, JSX, useMemo } from "react";

import {
    type ChatTask,
    selectChatAutopilot,
    selectChatNeglection,
    selectUnreadCount,
    useActiveChatMessages,
    useChatStore,
} from "@/sdk/agent/chat";
import { getSessionByTaskId } from "@/sdk/sessions/getters";

import classes from "../chat-components.module.css";
import { CountdownTimer } from "../CustomerSections/CountdownTimer";
import { ChatAvatar } from "./ChatAvatar";
import { ChatPreviewBuilder } from "./ChatPreviewBuilder";
import { SelectedChatIndicator } from "./SelectedChatIndicator";
import { ChatVariant, getChatVariant, getColorByVariant } from "./variants";

export interface QueuedChatProps {
    chat: ChatTask;
    onChatSelected: (chat: ChatTask) => void;
    isChatSelected: boolean;
}

/**
 * Returns the tooltip label for the chat. The tooltip need to be shown only if the chat is pending, neglected,
 * inactive, or is closing.
 * @param variant - The variant of the chat
 * @param isPending - Whether the chat is pending
 * @param changeStateAt - The timestamp when the chat state changed
 * @returns The tooltip label for the chat or undefined if no tooltip is needed
 */
function getTooltipLabel(variant: ChatVariant, chat: ChatTask): JSX.Element | undefined {
    const { changeStateAt } = chat;
    const isPending = chat.status === "pending";
    const isClosing = chat.status === "wrapping";
    if (variant === "inactive" && changeStateAt) {
        return (
            <span>
                Chat will close if they don&apos;t respond in{" "}
                <span style={{ display: "inline-flex" }}>
                    <CountdownTimer endAt={changeStateAt} includeUnit={true} />
                </span>
            </span>
        );
    } else if (variant === "neglected") {
        return <span>Chat has been neglected, respond soon to avoid a transfer.</span>;
    } else if (isClosing) {
        return <span>Chat closed—clearing it from your queue.</span>;
    } else if (isPending) {
        return <span>This chat cannot be selected until the customer is connected</span>;
    } else {
        return undefined;
    }
}

export const QueuedChat = forwardRef<HTMLButtonElement, QueuedChatProps>(
    ({ onChatSelected, chat, isChatSelected, ...rest }, ref) => {
        const messages = useActiveChatMessages(chat);
        const lastMessageDate = messages.at(-1)?.dateCreated ?? null;
        const unreadMessageCount = useChatStore(selectUnreadCount(chat.chatId));

        // Subscribe to neglection and autopilot state to trigger re-renders for concurrent chats
        const neglectionState = useChatStore(selectChatNeglection(chat.chatId));
        const autopilotState = useChatStore(selectChatAutopilot(chat.chatId));

        // Recalculate variant when neglection or autopilot state changes
        // oxlint-disable-next-line exhaustive-deps -- intentional: these trigger recalculation even though getChatVariant reads from the task
        const variant = useMemo(() => getChatVariant(chat), [chat, neglectionState, autopilotState]);
        const variantColor = useMemo(() => getColorByVariant(variant), [variant]);

        /** Memoized session ID that only changes when the task ID changes */
        const sessionId = useMemo(() => {
            const session = getSessionByTaskId(chat.id);
            if (!session) {
                throw new Error(`Session not found for task ID: ${chat.id} in chat queue`); // should be an impossible case
            }
            return session.id;
        }, [chat.id]);

        const isPending = chat.status === "pending";
        const tooltipLabel = useMemo(() => {
            return getTooltipLabel(variant, chat);
        }, [variant, chat]);

        return (
            <Tooltip
                label={tooltipLabel}
                position="right"
                withArrow
                disabled={!tooltipLabel}
                transitionProps={{ duration: 300 }}
                multiline
                w={200}
            >
                <UnstyledButton
                    ref={ref}
                    id={`chat-select-chatId:${chat.chatId || "ERROR_UNDEFINED_CHAT_ID"}`}
                    {...rest}
                    className={classes.queued_chat_interactive_area}
                    onClick={() => onChatSelected(chat)}
                    disabled={isPending}
                    data-selected={isChatSelected}
                    data-pending={isPending}
                >
                    <SelectedChatIndicator isChatSelected={isChatSelected} />
                    <ChatAvatar
                        sessionId={sessionId}
                        customerName={chat.customerName}
                        variant={variant}
                        variantColor={variantColor}
                        avatarSize={32}
                    />
                    <ChatPreviewBuilder
                        chat={chat}
                        lastMessageDate={lastMessageDate}
                        variant={variant}
                        variantColor={variantColor}
                        wrapper={Stack}
                        unreadMessageCount={unreadMessageCount}
                    />
                </UnstyledButton>
            </Tooltip>
        );
    },
);

QueuedChat.displayName = "QueuedChat";
